import { useEffect, useState } from "react";
import axios from "axios";
import useSeo from "@/hooks/useSeo";
import { toast } from "sonner";
import {
  ChevronRight, ClipboardCheck, Sparkles, MessageCircle, Stethoscope,
  ShieldCheck, Search, FileText,
} from "lucide-react";

const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

const WHATSAPP_NUMBER = (process.env.REACT_APP_LEAD_WHATSAPP || "").replace(/\D+/g, "");

// ─── Question widgets (mirror PublicMetabolicTestPage) ───────────────────
function ScaleInput({ value, onChange, id }) {
  return (
    <div className="flex gap-1 flex-wrap" data-testid={`scale-${id}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-sm border text-sm font-semibold transition-colors ${
            value === n ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
          }`}
          data-testid={`scale-${id}-${n}`}
        >
          {n}
        </button>
      ))}
      <span className="ml-2 self-center text-[11px] text-muted-foreground">1 = muito baixo · 5 = excelente</span>
    </div>
  );
}

function QuestionField({ q, value, onChange }) {
  if (q.type === "text") {
    return (
      <textarea
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
        data-testid={`q-${q.id}`}
      />
    );
  }
  if (q.type === "number") {
    return (
      <div className="flex items-center gap-2">
        <input
          type="number" step="0.1"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-32 px-3 py-2 rounded-sm border border-input bg-card text-sm"
          data-testid={`q-${q.id}`}
        />
        {q.unit && <span className="text-xs text-muted-foreground">{q.unit}</span>}
      </div>
    );
  }
  if (q.type === "scale5") {
    return <ScaleInput value={value} onChange={onChange} id={q.id} />;
  }
  if (q.type === "single") {
    return (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>
        {q.options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
              value === opt ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
            data-testid={`q-${q.id}-opt-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const next = arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt];
      onChange(next);
    };
    return (
      <div className="flex flex-wrap gap-2" data-testid={`q-${q.id}`}>
        {q.options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
              arr.includes(opt) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  return null;
}

// ─── Screen 1 — Intro form ───────────────────────────────────────────────
function IntroScreen({ onStart }) {
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);

  const start = async () => {
    if (!name.trim()) { toast.error("Digite seu nome."); return; }
    const digits = (whatsapp || "").replace(/\D+/g, "");
    if (digits.length < 10) { toast.error("Digite um WhatsApp válido (DDD + número)."); return; }
    setLoading(true);
    try {
      const { data } = await publicApi.post("/public/leads/metabolic-test/start", {
        name: name.trim(), whatsapp: digits,
      });
      onStart(data.token, name.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível iniciar o teste. Tente novamente.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10"
      data-testid="viva-melhor-intro">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-sm bg-primary text-primary-foreground grid place-items-center mx-auto mb-4">
            <Stethoscope className="w-7 h-7" />
          </div>
          <div className="overline text-gold mb-2 tracking-[0.24em]">Centro de Performance Farmacêutica</div>
          <h1 className="brand-wordmark uppercase text-3xl md:text-4xl font-bold text-primary leading-tight">
            Viva Melhor
          </h1>
          <p className="text-sm text-muted-foreground mt-4 max-w-sm mx-auto leading-relaxed">
            Descubra como sua performance metabólica pode melhorar.
            Digite seu nome e WhatsApp para começar.
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div>
            <label className="overline text-muted-foreground mb-1 block">Nome</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm"
              data-testid="lead-name-input"
              autoComplete="name"
            />
          </div>
          <div>
            <label className="overline text-muted-foreground mb-1 block">WhatsApp</label>
            <input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="(11) 99999-8888"
              className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-sm"
              data-testid="lead-whatsapp-input"
              inputMode="tel"
              autoComplete="tel"
            />
          </div>
          <button
            onClick={start}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            data-testid="lead-start-btn"
          >
            {loading ? "Preparando…" : (<><Sparkles className="w-4 h-4" /> Iniciar Teste</>)}
          </button>
          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            Ao continuar, você concorda em compartilhar suas respostas com nosso farmacêutico
            para receber um feedback personalizado.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 2 — Questionnaire (reuses the same rendering as /public/mt/:token) ──
function QuestionnaireScreen({ token, patientName, onDone }) {
  const [state, setState] = useState({ loading: true, error: null, test: null });
  const [answers, setAnswers] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/metabolic-test/${token}`);
        setState({ loading: false, error: null, test: data });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Não foi possível carregar o teste.", test: null });
      }
    })();
  }, [token]);
  if (state.loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground overline">Carregando…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="bg-card border border-destructive/30 rounded-sm p-6 max-w-md text-center">
          <h2 className="brand-wordmark uppercase text-lg font-bold mb-2">Ops</h2>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  const sections = state.test.questionnaire;
  const currentSection = sections[sectionIdx];
  const isLast = sectionIdx === sections.length - 1;

  const setAnswer = (qid, v) => setAnswers((prev) => ({ ...prev, [qid]: v }));

  const canProceed = () => currentSection.questions
    .filter((q) => q.required)
    .every((q) => {
      const v = answers[q.id];
      return v !== undefined && v !== "" && v !== null;
    });

  const next = () => {
    if (!canProceed()) { toast.error("Responda todas as perguntas obrigatórias."); return; }
    setSectionIdx((i) => i + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setSectionIdx((i) => Math.max(0, i - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    if (!canProceed()) { toast.error("Responda todas as perguntas obrigatórias."); return; }
    setSubmitting(true);
    try {
      await publicApi.post(`/public/metabolic-test/${token}/submit`, { answers });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar respostas."); }
    setSubmitting(false);
  };

  const pct = Math.round(((sectionIdx + 1) / sections.length) * 100);

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="viva-melhor-questionnaire">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="overline text-gold mb-2 inline-flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" /> Teste metabólico
          </div>
          <h1 className="brand-wordmark text-2xl md:text-3xl font-bold tracking-tight uppercase text-primary">
            Viva Melhor
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {patientName ? `Olá, ${patientName}! ` : ""}
            Suas respostas ajudam o farmacêutico a preparar um feedback personalizado. Leva ~5 minutos.
          </p>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-1 text-xs text-muted-foreground">
            <span>Seção {sectionIdx + 1} de {sections.length}</span>
            <span>{pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-sm overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-5 md:p-6">
          <div className="overline text-gold mb-4">{currentSection.title}</div>
          <div className="space-y-5">
            {currentSection.questions.map((q) => (
              <div key={q.id}>
                <label className="block text-sm font-medium mb-2">
                  {q.label} {q.required && <span className="text-destructive">*</span>}
                </label>
                <QuestionField q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <button
            onClick={back} disabled={sectionIdx === 0}
            className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="vm-back-btn"
          >
            Voltar
          </button>
          {!isLast ? (
            <button
              onClick={next}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
              data-testid="vm-next-btn"
            >
              Próximo <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit} disabled={submitting}
              className="px-6 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
              data-testid="vm-submit-btn"
            >
              {submitting ? "Enviando…" : "Enviar respostas"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Screen 3 — Smart result report ──────────────────────────────────────
function ScoreBadge({ value, bucket, size = "md" }) {
  const palette = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    red:   "bg-rose-100 text-rose-800 border-rose-300",
    muted: "bg-muted text-muted-foreground border-border",
  };
  const cls = palette[bucket] || palette.muted;
  const sizeCls = size === "lg"
    ? "px-4 py-2 text-2xl md:text-3xl font-bold rounded-md"
    : "px-2.5 py-1 text-sm font-semibold rounded-sm";
  return (
    <span className={`inline-flex items-baseline gap-1 border ${cls} ${sizeCls}`}>
      {value === null || value === undefined ? "—" : value}
      <span className="opacity-60 text-[0.65em] font-normal">/100</span>
    </span>
  );
}

function ResultReportScreen({ token, patientName }) {
  const [state, setState] = useState({ loading: true, error: null, report: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/metabolic-test/${token}/result`);
        setState({ loading: false, error: null, report: data });
      } catch (e) {
        setState({
          loading: false,
          error: e.response?.data?.detail || "Não foi possível carregar seu relatório.",
          report: null,
        });
      }
    })();
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground overline">Preparando seu relatório…</div>;
  }
  if (state.error || !state.report) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="bg-card border border-destructive/30 rounded-sm p-6 max-w-md text-center">
          <h2 className="brand-wordmark uppercase text-lg font-bold mb-2">Ops</h2>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  const report = state.report;
  const scoredSections = (report.sections || []).filter((s) => s.score !== null && s.score !== undefined);
  const hypotheses = report.hypotheses || [];
  const ctaMsg = `Olá, acabei de fazer o teste ${report.test_id} e quero agendar meu acompanhamento`;
  const whatsappHref = WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(ctaMsg)}`
    : null;

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="viva-melhor-report">
      <div className="max-w-2xl mx-auto">
        {/* Header — Protocol ID */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-sm bg-primary text-primary-foreground grid place-items-center mx-auto mb-3">
            <FileText className="w-6 h-6" />
          </div>
          <div className="overline text-gold mb-1 tracking-[0.24em] text-[10px]">Centro de Performance Farmacêutica</div>
          <h1 className="brand-wordmark uppercase text-2xl md:text-3xl font-bold text-primary leading-tight">
            Protocolo de Análise <span className="text-gold">#{report.test_id}</span>
          </h1>
          {patientName || report.patient_name ? (
            <p className="text-sm text-muted-foreground mt-2">
              Análise de <b className="text-foreground">{patientName || report.patient_name}</b>
            </p>
          ) : null}
        </div>

        {/* Global score */}
        <div className="bg-card border border-border rounded-md p-6 mb-6 text-center"
             data-testid="report-global-score">
          <div className="overline text-muted-foreground mb-3 text-[11px]">Pontuação metabólica global</div>
          <ScoreBadge value={report.global_score} bucket={report.global_bucket} size="lg" />
          <p className="text-xs text-muted-foreground mt-3 max-w-sm mx-auto leading-relaxed">
            Escala orientativa: <b className="text-emerald-700">verde &gt; 80</b> ·
            <b className="text-amber-700"> amarelo 50–80</b> ·
            <b className="text-rose-700"> vermelho &lt; 50</b>.
          </p>
        </div>

        {/* Per-section cards */}
        <div className="space-y-3 mb-6">
          {scoredSections.map((s) => (
            <div key={s.id}
                 className="bg-card border border-border rounded-md p-4 md:p-5"
                 data-testid={`report-section-${s.id}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="text-base md:text-lg font-semibold text-primary">{s.title}</h3>
                <ScoreBadge value={s.score} bucket={s.bucket} />
              </div>
              {s.interpretation && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {s.interpretation}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Nutrient hypotheses */}
        {hypotheses.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-5 mb-6"
               data-testid="report-hypotheses">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-amber-700" />
              <h3 className="text-base font-semibold text-amber-900">Possíveis Desequilíbrios</h3>
            </div>
            <p className="text-sm text-amber-900/80 leading-relaxed mb-3">
              Com base nos sintomas relatados, há sinais sugestivos de carência de:
            </p>
            <ul className="space-y-1.5">
              {hypotheses.slice(0, 6).map((h) => (
                <li key={h.code} className="flex items-baseline gap-2 text-sm">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600 shrink-0 translate-y-[-2px]" />
                  <span>
                    <b className="text-amber-950">{h.label}</b>
                    <span className="text-amber-800/70"> — {h.count} sintoma{h.count > 1 ? "s" : ""} associado{h.count > 1 ? "s" : ""}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sales CTA */}
        <div className="bg-primary/5 border border-primary/20 rounded-md p-5 md:p-6 text-center"
             data-testid="report-cta">
          <ShieldCheck className="w-8 h-8 text-primary mx-auto mb-3" />
          <p className="text-sm md:text-base text-foreground leading-relaxed max-w-md mx-auto">
            Estas hipóteses precisam de <b>confirmação clínica</b>. Agende sua consulta para
            montar seu <b>protocolo de precisão</b>.
          </p>
          <a
            href={whatsappHref || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { if (!whatsappHref) e.preventDefault(); }}
            className={`mt-5 inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm md:text-base font-semibold transition-colors w-full sm:w-auto min-h-[48px] ${
              whatsappHref
                ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm active:scale-[0.99]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
            data-testid="report-whatsapp-cta"
          >
            <MessageCircle className="w-5 h-5" /> Agendar minha consulta no WhatsApp
          </a>
          {!whatsappHref && (
            <p className="text-[10px] text-muted-foreground mt-3 italic">
              Número do WhatsApp ainda não configurado (REACT_APP_LEAD_WHATSAPP).
            </p>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-6 italic max-w-md mx-auto leading-relaxed">
          Este relatório é orientativo e não substitui avaliação clínica presencial.
        </p>
      </div>
    </div>
  );
}

// ─── Root page ───────────────────────────────────────────────────────────
export { ResultReportScreen };

export default function VivaMelhorPage() {
  const [step, setStep] = useState("intro"); // intro | questionnaire | done
  useSeo({
    title: "Saúde Intestinal em Tucuruí | Teste Viva Melhor",
    description: "Teste gratuito de saúde intestinal em Tucuruí: identifique sinais de disbiose intestinal, SIBO, síndrome do intestino irritável, constipação intestinal e corrimento recorrente, com orientação de farmacêutico clínico.",
    keywords: "saúde intestinal Tucuruí, disbiose intestinal, síndrome do intestino irritável Tucuruí, SIBO tratamento, constipação intestinal, corrimento recorrente tratamento, farmacêutico clínico Tucuruí-PA",
    path: "/viva-melhor",
  });
  const [token, setToken] = useState(null);
  const [patientName, setPatientName] = useState("");

  if (step === "done" && token) return <ResultReportScreen token={token} patientName={patientName} />;
  if (step === "questionnaire" && token) {
    return (
      <QuestionnaireScreen
        token={token}
        patientName={patientName}
        onDone={() => setStep("done")}
      />
    );
  }
  return (
    <IntroScreen
      onStart={(newToken, name) => {
        setToken(newToken);
        setPatientName(name);
        setStep("questionnaire");
      }}
    />
  );
}
